
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { creditsEvents } from '@/utils/creditsEvents';

interface BonusCreditGrantProps {
  userId: string;
  userEmail: string | null;
  onGrantComplete: () => void;
}

export function BonusCreditGrant({ userId, userEmail, onGrantComplete }: BonusCreditGrantProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [granting, setGranting] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    expirationDays: '0',
    description: '',
    adminNotes: ''
  });
  const { toast } = useToast();

  const handleGrantCredits = async () => {
    if (!formData.amount || isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid positive number",
        variant: "destructive",
      });
      return;
    }

    try {
      setGranting(true);
      
      const { data, error } = await supabase.functions.invoke('grant-bonus-credits', {
        body: {
          user_id: userId,
          amount: Number(formData.amount),
          expiration_days: Number(formData.expirationDays) || 0,
          description: formData.description || `Admin granted ${formData.amount} bonus credits`,
          admin_notes: formData.adminNotes || undefined
        }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to grant bonus credits');
      }

      toast({
        title: "Bonus Credits Granted",
        description: `Successfully granted ${formData.amount} bonus credits to ${userEmail}`,
      });

      // Reset form and close dialog
      setFormData({
        amount: '',
        expirationDays: '0',
        description: '',
        adminNotes: ''
      });
      setIsDialogOpen(false);
      
      // Trigger refresh of parent data
      onGrantComplete();

      // Emit event to update other components
      creditsEvents.emit({ userId, action: 'grant' });

    } catch (error: any) {
      console.error('Error granting bonus credits:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to grant bonus credits",
        variant: "destructive",
      });
    } finally {
      setGranting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5" />
          Grant Bonus Credits
        </CardTitle>
        <CardDescription>
          Manually grant bonus credits to {userEmail}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full">
              <Gift className="h-4 w-4 mr-2" />
              Grant Bonus Credits
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Grant Bonus Credits</DialogTitle>
              <DialogDescription>
                Grant additional bonus credits to {userEmail}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Credit Amount</Label>
                <Input
                  type="number"
                  placeholder="Enter number of credits"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">
                  Number of bonus credits to grant
                </p>
              </div>

              <div className="space-y-2">
                <Label>Expiration Days</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.expirationDays}
                  onChange={(e) => setFormData({...formData, expirationDays: e.target.value})}
                  min="0"
                />
                <p className="text-xs text-muted-foreground">
                  Days until credits expire (0 = never expire)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="Reason for granting credits (visible to user)"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  placeholder="Internal notes (not visible to user)"
                  value={formData.adminNotes}
                  onChange={(e) => setFormData({...formData, adminNotes: e.target.value})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleGrantCredits}
                disabled={granting || !formData.amount}
              >
                {granting ? 'Granting...' : 'Grant Credits'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
